<script setup lang="ts">
/**
 * LeafletAddressPicker
 * --------------------
 * Arama input'u + Leaflet/OpenStreetMap haritası.
 *  - Kullanıcı adres yazar -> Nominatim autocomplete önerileri
 *  - Öneri seçilince harita o noktaya gider + marker konur
 *  - Harita'ya tıklayınca ya da marker sürüklenince reverse geocode yapılır
 *  - Component dışına (update:modelValue) { lat, lng, display_name } emit edilir
 *
 * Nominatim kullanım politikası:
 *  - Max 1 req/sec (debounce: 400ms yeterli)
 *  - Kendi User-Agent/Referer'ı ile çağrılmalı; ticari yoğun kullanımda self-host.
 */

import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

// Leaflet default icon path fix (bundler'da URL bozulur).
// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow
})

interface Picked {
    lat: number
    lng: number
    display_name: string
    address?: Record<string, string>
}

const props = withDefaults(
    defineProps<{
        modelValue?: Picked | null
        initialCenter?: [number, number] // [lat, lng]
        initialZoom?: number
        countryCodes?: string // "tr,fr" gibi Nominatim filtresi
    }>(),
    {
        modelValue: null,
        initialCenter: () => [41.0082, 28.9784], // Istanbul default
        initialZoom: 13,
        countryCodes: 'tr,fr'
    }
)

const emit = defineEmits<{
    (e: 'update:modelValue', v: Picked | null): void
}>()

const { t } = useI18n()

const mapContainer = ref<HTMLDivElement | null>(null)
const searchText = ref('')
const suggestions = ref<any[]>([])
const showSuggestions = ref(false)
const loading = ref(false)

let map: L.Map | null = null
let marker: L.Marker | null = null
let searchDebounceId: number | null = null
// refactored — edit modunda display_name'i input'a yazarken watcher otomatik Nominatim
// araması yapmasın diye tek seferlik atlama bayrağı. Aksi halde kullanıcının
// kaydettiği lat/lng yerine "aranan metne göre" bir suggestion açılıyor gibi görünüyor.
let skipNextSearch = false

// ------------------------------------------------------------------
// Nominatim helpers
// ------------------------------------------------------------------

const NOMINATIM_BASE = 'https://nominatim.openstreetmap.org'

async function nominatimSearch(q: string): Promise<any[]> {
    if (!q || q.length < 3) return []
    const url = `${NOMINATIM_BASE}/search?format=json&addressdetails=1&limit=6&q=${encodeURIComponent(q)}&countrycodes=${props.countryCodes}`
    const res = await fetch(url, {
        headers: { Accept: 'application/json' }
    })
    if (!res.ok) return []
    return await res.json()
}

async function nominatimReverse(lat: number, lng: number): Promise<any | null> {
    const url = `${NOMINATIM_BASE}/reverse?format=json&addressdetails=1&lat=${lat}&lon=${lng}`
    const res = await fetch(url, {
        headers: { Accept: 'application/json' }
    })
    if (!res.ok) return null
    return await res.json()
}

// ------------------------------------------------------------------
// Map interactions
// ------------------------------------------------------------------

function setMarker(lat: number, lng: number) {
    if (!map) return
    if (!marker) {
        marker = L.marker([lat, lng], { draggable: true }).addTo(map)
        marker.on('dragend', async () => {
            const p = marker!.getLatLng()
            await handleCoordinatePick(p.lat, p.lng)
        })
    } else {
        marker.setLatLng([lat, lng])
    }
}

async function handleCoordinatePick(lat: number, lng: number) {
    setMarker(lat, lng)
    loading.value = true
    try {
        const r = await nominatimReverse(lat, lng)
        if (r) {
            searchText.value = r.display_name || ''
            emit('update:modelValue', {
                lat,
                lng,
                display_name: r.display_name,
                address: r.address
            })
        } else {
            emit('update:modelValue', { lat, lng, display_name: '' })
        }
    } finally {
        loading.value = false
    }
}

// refactored — template içinden global setTimeout'a erişim yok; blur sonrası
// suggestion click'in kaybolmaması için küçük gecikmeli kapatma helper'ı.
function hideSuggestionsDelayed() {
    window.setTimeout(() => {
        showSuggestions.value = false
    }, 150)
}

function selectSuggestion(s: any) {
    const lat = parseFloat(s.lat)
    const lng = parseFloat(s.lon)
    if (!map) return
    map.setView([lat, lng], 17)
    setMarker(lat, lng)
    searchText.value = s.display_name
    showSuggestions.value = false
    emit('update:modelValue', {
        lat,
        lng,
        display_name: s.display_name,
        address: s.address
    })
}

// ------------------------------------------------------------------
// Watchers & lifecycle
// ------------------------------------------------------------------

watch(searchText, (val) => {
    if (skipNextSearch) {
        skipNextSearch = false
        return
    }
    if (searchDebounceId) {
        window.clearTimeout(searchDebounceId)
    }
    if (!val || val.length < 3) {
        suggestions.value = []
        showSuggestions.value = false
        return
    }
    searchDebounceId = window.setTimeout(async () => {
        loading.value = true
        try {
            suggestions.value = await nominatimSearch(val)
            showSuggestions.value = suggestions.value.length > 0
        } finally {
            loading.value = false
        }
    }, 400)
})

onMounted(() => {
    if (!mapContainer.value) return

    const hasSaved = !!props.modelValue
    const start: [number, number] = hasSaved
        ? [props.modelValue!.lat, props.modelValue!.lng]
        : props.initialCenter
    // refactored — kaydedilmiş bir nokta varsa sokak seviyesinde (17) aç; değilse
    // default şehir zoom'u. Böylece müşterinin kendi seçtiği nokta net görünür.
    const zoom = hasSaved ? 17 : props.initialZoom

    map = L.map(mapContainer.value).setView(start, zoom)

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap',
        maxZoom: 19
    }).addTo(map)

    map.on('click', async (e: L.LeafletMouseEvent) => {
        await handleCoordinatePick(e.latlng.lat, e.latlng.lng)
    })

    if (hasSaved) {
        setMarker(props.modelValue!.lat, props.modelValue!.lng)
        // refactored — display_name'i input'a yazarken Nominatim'e istek atılmasın;
        // kullanıcı kendi kaydettiği noktayı görmeli, yeni öneri değil.
        skipNextSearch = true
        searchText.value = props.modelValue!.display_name || ''
    }

    // Leaflet bazen modal içinde yanlış boyut ölçüyor; küçük bir gecikmeden sonra yenile.
    setTimeout(() => {
        map?.invalidateSize()
        // invalidateSize setView'ı bozabilir; kaydedilmiş noktayı tekrar merkeze al.
        if (hasSaved && map) {
            map.setView([props.modelValue!.lat, props.modelValue!.lng], 17)
        }
    }, 200)
})

onBeforeUnmount(() => {
    if (searchDebounceId) window.clearTimeout(searchDebounceId)
    if (map) {
        map.remove()
        map = null
    }
})
</script>

<template>
    <div class="leaflet-picker">
        <div class="picker-search-wrap">
            <input
                type="text"
                v-model="searchText"
                :placeholder="t('addressSearchPlaceholder')"
                class="picker-search-input"
                @focus="showSuggestions = suggestions.length > 0"
                @blur="hideSuggestionsDelayed"
            />
            <ul v-if="showSuggestions" class="picker-suggestions">
                <li
                    v-for="s in suggestions"
                    :key="s.place_id"
                    class="picker-suggestion-item"
                    @mousedown.prevent="selectSuggestion(s)"
                >
                    {{ s.display_name }}
                </li>
            </ul>
            <span v-if="loading" class="picker-loading">…</span>
        </div>

        <div ref="mapContainer" class="picker-map"></div>

        <p class="picker-hint">{{ t('addressMapHint') }}</p>
    </div>
</template>

<style scoped>
.leaflet-picker {
    @apply w-full flex flex-col gap-2;
}

.picker-search-wrap {
    @apply relative w-full;
}

.picker-search-input {
    @apply w-full py-2 px-3 rounded-md border-2 border-t-border bg-t-card text-t-text outline-none;
    @apply happy-ifo-dark:bg-t-accent/5
           happy-ifo-dark:border
           happy-ifo-dark:border-t-accent/30
           happy-ifo-dark:rounded-full
           happy-ifo-dark:placeholder:text-t-text-muted;
}

.picker-search-input:focus {
    @apply border-t-accent;
    @apply happy-ifo-dark:bg-t-accent/10 happy-ifo-dark:shadow-[0_0_8px_rgba(212,160,23,0.3)];
}

.picker-suggestions {
    @apply absolute left-0 right-0 top-full mt-1 z-[1001] max-h-60 overflow-y-auto rounded-md border bg-t-card text-t-text border-t-border shadow-md;
    @apply happy-ifo-dark:bg-t-bg happy-ifo-dark:border-t-accent/30;
}

.picker-suggestion-item {
    @apply px-3 py-2 text-sm cursor-pointer border-b border-t-border last:border-b-0;
}

.picker-suggestion-item:hover {
    @apply bg-t-accent/10;
}

.picker-loading {
    @apply absolute right-3 top-1/2 -translate-y-1/2 text-t-accent;
}

.picker-map {
    @apply w-full h-64 rounded-md overflow-hidden border border-t-border z-0;
    @apply happy-ifo-dark:border-t-accent/30;
}

.picker-hint {
    @apply text-xs text-t-text-muted;
}
</style>
